import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import DashboardLayout from './components/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Pages
import LoginPageRBAC from './pages/auth/LoginPageRBAC';
import RegisterPage from './pages/auth/RegisterPage';
import PaymentPage from './pages/auth/PaymentPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyResetOtpPage from './pages/auth/VerifyResetOtpPage';

// Lazy-loaded dashboard pages
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdmin/Dashboard'));
const TenantManagement = lazy(() => import('./pages/SuperAdmin/TenantManagement'));
const PaymentTracking = lazy(() => import('./pages/SuperAdmin/PaymentTracking'));
const SystemLogs = lazy(() => import('./pages/SuperAdmin/SystemLogs'));
const SystemHealth = lazy(() => import('./pages/SuperAdmin/SystemHealth'));
const SecurityEvents = lazy(() => import('./pages/SuperAdmin/SecurityEvents'));
const AnnouncementManagement = lazy(() => import('./pages/SuperAdmin/AnnouncementManagement'));
const PlanManagement = lazy(() => import('./pages/SuperAdmin/PlanManagement'));
const Reports = lazy(() => import('./pages/SuperAdmin/Reports'));
const UserManagement = lazy(() => import('./pages/SuperAdmin/UserManagement'));

const CalendarPage = lazy(() => import('./pages/Calendar'));
const HotelAdminDashboard = lazy(() => import('./pages/HotelAdmin/Dashboard'));
const HotelRooms = lazy(() => import('./pages/HotelAdmin/Rooms'));
const HotelGuests = lazy(() => import('./pages/HotelAdmin/Guests'));
const HotelBookings = lazy(() => import('./pages/HotelAdmin/Bookings'));
const HotelUpcomingArrivals = lazy(() => import('./pages/Bookings/UpcomingArrivalsPage'));
const HotelEmployees = lazy(() => import('./pages/HotelAdmin/Employees'));
const HotelPayroll = lazy(() => import('./pages/HotelAdmin/Payroll'));
const HotelPayments = lazy(() => import('./pages/HotelAdmin/Payments'));
const HotelReports = lazy(() => import('./pages/HotelAdmin/Reports'));
const HotelPricing = lazy(() => import('./pages/HotelAdmin/Pricing'));
const HotelAccounting = lazy(() => import('./pages/HotelAdmin/Accounting'));
const HotelAdminSettings = lazy(() => import('./pages/HotelAdmin/Settings'));
const HotelAttendance = lazy(() => import('./pages/HotelAdmin/Attendance'));

const EmployeeDashboard = lazy(() => import('./pages/Employee/Dashboard'));
const EmployeeTasks = lazy(() => import('./pages/Employee/Tasks'));
const EmployeeSchedule = lazy(() => import('./pages/Employee/Schedule'));
const EmployeeProfile = lazy(() => import('./pages/Employee/Profile'));

// Test Navigation
import TestNavigation from './pages/TestNavigation';

// Marketing / Public Pages
import LandingPage from './pages/LandingPage';

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
      <p className="mt-3 text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

// Domain detection: "app" domains show the SaaS, everything else shows marketing
const isAppDomain = () => {
  const h = window.location.hostname;
  return h.startsWith('hotels.') || h === 'localhost' || h === '127.0.0.1';
};

function AppWithRBAC() {
  return (
    <AuthProvider>
      <div>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPageRBAC />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-reset-otp" element={<VerifyResetOtpPage />} />

          {/* Root route: landing page on marketing domain, redirect to login on app domain */}
          <Route path="/" element={isAppDomain() ? <Navigate to="/login" replace /> : <LandingPage />} />
          <Route path="/test" element={<TestNavigation />} />

          {/* Super Admin Routes */}
          <Route path="/super-admin" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><SuperAdminDashboard /></DashboardLayout></ProtectedRoute>} />
          <Route path="/tenants" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><TenantManagement /></DashboardLayout></ProtectedRoute>} />
          <Route path="/system-payments" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><PaymentTracking /></DashboardLayout></ProtectedRoute>} />
          <Route path="/system-logs" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><SystemLogs /></DashboardLayout></ProtectedRoute>} />
          <Route path="/system-health" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><SystemHealth /></DashboardLayout></ProtectedRoute>} />
          <Route path="/security-events" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><SecurityEvents /></DashboardLayout></ProtectedRoute>} />
          <Route path="/announcements" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><AnnouncementManagement /></DashboardLayout></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><PlanManagement /></DashboardLayout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><Reports /></DashboardLayout></ProtectedRoute>} />
          <Route path="/system-users" element={<ProtectedRoute requiredRole="super_admin"><DashboardLayout><UserManagement /></DashboardLayout></ProtectedRoute>} />

          {/* Hotel Admin Routes */}
          <Route path="/dashboard" element={<ProtectedRoute requiredRole="admin"><DashboardLayout><HotelAdminDashboard /></DashboardLayout></ProtectedRoute>} />
          <Route path="/rooms" element={<ProtectedRoute requiredRole={['admin', 'staff']}><DashboardLayout><HotelRooms /></DashboardLayout></ProtectedRoute>} />
          <Route path="/guests" element={<ProtectedRoute requiredRole={['admin', 'staff']}><DashboardLayout><HotelGuests /></DashboardLayout></ProtectedRoute>} />
          <Route path="/bookings" element={<ProtectedRoute requiredRole={['admin', 'staff']}><DashboardLayout><HotelBookings /></DashboardLayout></ProtectedRoute>} />
          <Route path="/upcoming-arrivals" element={<ProtectedRoute requiredRole={['admin', 'staff']}><DashboardLayout><HotelUpcomingArrivals /></DashboardLayout></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute requiredRole="admin"><DashboardLayout><HotelEmployees /></DashboardLayout></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute requiredRole="admin" requiredFeature="payroll"><DashboardLayout><HotelPayroll /></DashboardLayout></ProtectedRoute>} />
          <Route path="/hotel-payments" element={<ProtectedRoute requiredRole="admin" requiredFeature="payments"><DashboardLayout><HotelPayments /></DashboardLayout></ProtectedRoute>} />
          <Route path="/pricing" element={<ProtectedRoute requiredRole="admin" requiredFeature="seasonal_pricing"><DashboardLayout><HotelPricing /></DashboardLayout></ProtectedRoute>} />
          <Route path="/accounting" element={<ProtectedRoute requiredRole="admin" requiredFeature="accounting"><DashboardLayout><HotelAccounting /></DashboardLayout></ProtectedRoute>} />
          <Route path="/hotel-reports" element={<ProtectedRoute requiredRole="admin" requiredFeature="reports"><DashboardLayout><HotelReports /></DashboardLayout></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute requiredRole="admin"><DashboardLayout><HotelAttendance /></DashboardLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requiredRole="admin"><DashboardLayout><HotelAdminSettings /></DashboardLayout></ProtectedRoute>} />

          {/* Shared calendar reference page - open to all authenticated users */}
          <Route path="/calendar" element={<ProtectedRoute><DashboardLayout><CalendarPage /></DashboardLayout></ProtectedRoute>} />

          {/* Employee Routes */}
          <Route path="/employee-dashboard" element={<ProtectedRoute requiredRole="staff"><DashboardLayout><EmployeeDashboard /></DashboardLayout></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute requiredRole="staff"><DashboardLayout><EmployeeTasks /></DashboardLayout></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute requiredRole="staff"><DashboardLayout><EmployeeSchedule /></DashboardLayout></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute requiredRole="staff"><DashboardLayout><EmployeeProfile /></DashboardLayout></ProtectedRoute>} />

          {/* 404 Page */}
          <Route path="*" element={
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
              <div className="text-center p-10 bg-white rounded-lg shadow-sm">
                <div className="text-5xl mb-5">🔍</div>
                <h2 className="text-xl font-bold mb-2.5">
                  Page Not Found
                </h2>
                <p className="text-gray-500 mb-5">
                  The page you're looking for doesn't exist.
                </p>
                <button
                  onClick={() => window.location.href = '/dashboard'}
                  className="px-5 py-2.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          } />
          </Routes>
        </Suspense>
      </div>
    </AuthProvider>
  );
}

export default AppWithRBAC;
